import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as yup from 'yup'
import { Formik } from 'formik'
import { useNewTranslation } from 'hooks'
import { updateTeam, TeamState, updateTeamType, getRoles } from 'state/teams'
import { MODAL_TYPE } from 'components/Team/common/Modals'
import { CreateTeamValuesState } from './index'
import styled from 'particles/themes/styled'
import { TeamTypeSelectField, ToggleAiCaptionReview } from './CreateTeam'
import Modal from 'particles/Modal'
import { FormikTextField } from 'particles/TextInputs/TextField'
import { FormikNumberInput } from 'particles/NumberInput'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import { useTranslation } from 'react-i18next'
import _ from 'lodash'
import { FormikToggle } from 'particles/Toggle'
import { selectIsInternalUser } from 'state/profile'
import { usePermission } from 'hooks/permissions'
import { OtherExternalTeamManagement } from 'components/Shared/Permission/enums'
interface CreateEditTeamModalProps {
    onClose: () => void
    type: MODAL_TYPE
    team?: TeamState
    className?: string
}

const EditTeam: React.FC<CreateEditTeamModalProps> = ({ onClose, type, team, className }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const [isConfirmingPermission, setIsConfirmingPermission] = useState(false)
    const [showError, setShowError] = useState(false)
    const isInternalUser = useSelector(selectIsInternalUser)
    const canManageRoles = usePermission(OtherExternalTeamManagement.ManageRoles) && !team?.is_internal
    const isAiCaptionReviewReadonly = isInternalUser && !canManageRoles
    const isAiCaptionReviewShow = isInternalUser && canManageRoles
    const isEditTeamType = type === MODAL_TYPE.EDIT_TEAM_TYPE

    const validationSchemaTeamName = yup.object({
        name: yup.string().nullable().required(t('errorMessage.validation.enterTeamName')),
    })

    const validationSchemaTeamType = yup.object({
        service_fee_percentage: yup
            .number()
            .nullable()
            .test({
                message: () => t('manageTeams.allTeams.defaultPartipostFeeOverRangeError'),
                test: value => {
                    if (value > 100 || value < 0) {
                        return false
                    }
                    return true
                },
            })
            .required(t('manageTeams.allTeams.defaultPartipostFeeOverRangeError')),
        type: yup.string().nullable().required(),
        reset_role_permissions: yup
            .boolean()
            .nullable()
            .required(t('campaign.template.instagramStoryNonUGC.customisedStickerText.errorMessage.empty')),
        ambassador_enabled: yup.boolean().nullable(),
    })

    const teamTypeInitialValue = {
        service_fee_percentage: team?.service_fee_percentage ?? undefined,
        type: team?.type || 'enterprise_self_serve',
        ambassador_enabled: team?.ambassador_enabled,
        is_ai_caption_review_enabled: team?.is_ai_caption_review_enabled ?? false,
    }

    const teamNameInitialValue = {
        name: team?.name,
    }
    const onSubmit = (values: CreateTeamValuesState) => {
        if (team) {
            if (isEditTeamType) {
                dispatch(
                    updateTeamType({
                        service_fee_percentage: values.service_fee_percentage,
                        type: values.type,
                        reset_role_permissions: values?.reset_role_permissions,
                        ambassador_enabled: values?.ambassador_enabled ?? false,
                        teamUuid: team.uuid,
                        is_ai_caption_review_enabled: values.is_ai_caption_review_enabled,
                        onSuccessCallBack: () =>
                            dispatch(
                                getRoles({
                                    teamUuid: team.uuid,
                                }),
                            ),
                    }),
                )
            } else {
                dispatch(
                    updateTeam({
                        name: values.name,
                        teamUuid: team.uuid,
                    }),
                )
            }
        }
        onClose()
    }

    return (
        <div className={className}>
            <Formik
                enableReinitialize={true}
                initialValues={isEditTeamType ? teamTypeInitialValue : teamNameInitialValue}
                validationSchema={isEditTeamType ? validationSchemaTeamType : validationSchemaTeamName}
                onSubmit={(values: CreateTeamValuesState) => {
                    onSubmit(values)
                }}
            >
                {formikBag => (
                    <Modal
                        onClose={onClose}
                        title={
                            isConfirmingPermission
                                ? t('modalWindow.keepCurrentPermissionSettingTitle')
                                : t('modalWindow.editTeamWindowTitle')
                        }
                        primaryButtonProps={{
                            label: isEditTeamType ? t('button.confirm') : t('button.saveChanges'),
                            type: 'submit',
                        }}
                        onConfirm={() => {
                            formikBag.validateForm().then(errors => {
                                const errKeys = Object.keys(errors)
                                const isTeamTypeEdited = formikBag.values?.type !== teamTypeInitialValue.type
                                if (_.isEmpty(errors)) {
                                    if (isConfirmingPermission || !isEditTeamType) {
                                        formikBag.submitForm()
                                    }
                                } else if (
                                    errKeys.length === 1 &&
                                    errKeys[0] === 'reset_role_permissions' &&
                                    !isConfirmingPermission
                                ) {
                                    if (!isTeamTypeEdited) {
                                        formikBag.setFieldValue('reset_role_permissions', false)
                                        formikBag.submitForm()
                                    } else {
                                        setIsConfirmingPermission(true)
                                    }
                                } else {
                                    setShowError(true)
                                }
                            })
                        }}
                        hasSecondaryButton={isEditTeamType}
                        secondaryButtonProps={{
                            label: isEditTeamType ? t('helper.uploadProgress.inProgress.button.cta1') : undefined,
                            onClick: onClose,
                        }}
                        subtext={
                            isConfirmingPermission ? t('modalWindow.keepCurrentPermissionSettingSubtext') : undefined
                        }
                        size="m"
                        hasIcon={isConfirmingPermission}
                    >
                        <FormFields
                            isConfirmingPermissions={isConfirmingPermission}
                            isEditTeamType={isEditTeamType}
                            showError={showError}
                            isAiCaptionReviewReadonly={isAiCaptionReviewReadonly}
                            isAiCaptionReviewShow={isAiCaptionReviewShow}
                        />
                    </Modal>
                )}
            </Formik>
        </div>
    )
}

export default styled(EditTeam)`
    .text-area {
        min-height: 16px;
    }
`

interface FormFieldsProps {
    isEditTeamType: boolean
    isConfirmingPermissions: boolean
    showError: boolean
    isAiCaptionReviewReadonly: boolean
    isAiCaptionReviewShow: boolean
}

const FormFields = ({
    isConfirmingPermissions,
    isEditTeamType,
    showError,
    isAiCaptionReviewReadonly,
    isAiCaptionReviewShow,
}: FormFieldsProps) => {
    const [t] = useTranslation()
    const canSeeAmbassadorToggle = usePermission(OtherExternalTeamManagement.ManageRoles)

    if (isConfirmingPermissions) {
        return (
            <FormikRadioButtonGroup
                name="reset_role_permissions"
                options={[
                    {
                        label: t('modalWindow.keepCurrentPermissionSetting.yesOption'),
                        value: false,
                    },
                    {
                        label: t('modalWindow.keepCurrentPermissionSetting.noOption'),
                        value: true,
                    },
                ]}
                showError={showError}
                validateByTouch={false}
            />
        )
    }
    if (isEditTeamType) {
        return (
            <>
                <TeamTypeSelectField showError={showError} />

                <FormikNumberInput
                    name="service_fee_percentage"
                    label={t('manageTeams.allTeams.defaultPartipostFeeTitle')}
                    subtext={t('manageTeams.allTeams.defaultPartipostFeeSubtext')}
                    placeholder={'0'}
                    isRequired
                    width="100%"
                    showErrorStyle={showError}
                />
                {isAiCaptionReviewShow && <ToggleAiCaptionReview isReadOnly={isAiCaptionReviewReadonly} />}
                {canSeeAmbassadorToggle && (
                    <FormikToggle
                        name="ambassador_enabled"
                        label={t('manageTeams.allTeams.enableAmbassadorsTrackerTitle')}
                        subtext={t('manageTeams.allTeams.enableAmbassadorsTrackerSubText')}
                    />
                )}
            </>
        )
    }

    return (
        <FormikTextField
            name="name"
            label={t('modalWindow.enterTeamNameHeader')}
            subtext={t('modalWindow.enterTeamNameSubtext')}
            placeholder={t('placeholderText.enterTeamName')}
            isRequired
            width="100%"
            showError={showError}
        />
    )
}
